import { useState, useCallback, useEffect } from 'react';
import { FormData, City, Warehouse } from '../types';
import { searchCities, getWarehouses } from '../services/api';
import { validateForm } from '../utils/validation';
import { submitShipping } from './sessionApi';

const initialFormData: FormData = {
  city: null,
  warehouse: null,
  name: '',
  phone: '',
};

export const useSessionForm = (orderId: string) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [cities, setCities] = useState<City[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [cityQuery, setCityQuery] = useState('');
  const [warehouseQuery, setWarehouseQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  const [payUrl, setPayUrl] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // NOTE: the ~20 console.log calls this hook inherited from the fork were
  // stripped, along with the four state-logging useEffects that existed only to
  // print them. This page handles money and runs on buyers' phones — form
  // contents, addresses and phone numbers do not belong in a browser console.
  // The console.error calls below are kept: they report real failures.

  // Handle city search
  useEffect(() => {
    const fetchCities = async () => {
      if (cityQuery.length < 2) {
        setCities([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await searchCities(cityQuery);
        setCities(results);
      } catch (error) {
        console.error('Error fetching cities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchCities, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [cityQuery]);

  // Handle warehouse fetch when city changes or warehouse query changes
  useEffect(() => {
    const fetchWarehouses = async () => {
      if (!formData.city) {
        setWarehouses([]);
        return;
      }

      setIsLoading(true);
      try {
        const results = await getWarehouses(formData.city.Ref, warehouseQuery);
        setWarehouses(results);
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWarehouses();
  }, [formData.city, warehouseQuery]);

  // Handle input changes
  const handleInputChange = useCallback((name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear warehouse when city changes
    if (name === 'city') {
      setFormData((prev) => ({ ...prev, warehouse: null }));
      setWarehouseQuery(''); // Reset warehouse query when city changes
    }

    // Clear error when field is updated
    setErrors((prev) => ({ ...prev, [name]: null }));
  }, []);

  // Handle city search input
  const handleCitySearch = useCallback((query: string) => {
    setCityQuery(query);
  }, []);

  // Handle warehouse search input
  const handleWarehouseSearch = useCallback((query: string) => {
    setWarehouseQuery(query);
  }, []);

  // Validate form fields
  const validateField = useCallback((name: keyof FormData) => {
    const validationResults = validateForm(formData);
    setErrors((prev) => ({ ...prev, [name]: validationResults[name] }));
    return !validationResults[name]; // Return true if valid
  }, [formData]);

  // Validate current step
  const validateStep = useCallback((currentStep: number): boolean => {
    const validationResults = validateForm(formData);

    switch (currentStep) {
      case 1:
        setErrors((prev) => ({ ...prev, city: validationResults.city }));
        return !validationResults.city;
      case 2:
        setErrors((prev) => ({ ...prev, warehouse: validationResults.warehouse }));
        return !validationResults.warehouse;
      case 3:
        setErrors((prev) => ({
          ...prev,
          name: validationResults.name,
          phone: validationResults.phone
        }));
        return !validationResults.name && !validationResults.phone;
      default:
        return false;
    }
  }, [formData]);

  // Handle next step
  const handleNextStep = useCallback((skipValidation = false) => {
    if (skipValidation || validateStep(step)) {
      setStep((prevStep) => prevStep + 1);
    }
    // Validation failed: stay on the current step. The field-level error is
    // already rendered in place by validateStep.
  }, [formData, step, validateStep]);

  // Handle previous step
  const handlePrevStep = useCallback(() => {
    if (step > 1) {
      setStep(step - 1);
    }
  }, [step]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    // Validate every step, not just the last: city and warehouse live on steps 1-2,
    // and the submit body dereferences them. Send the buyer back to the first
    // incomplete step so they see the existing localized field error in place.
    if (!validateStep(1)) { setStep(1); return; }
    if (!validateStep(2)) { setStep(2); return; }
    if (!validateStep(3)) return;

    setIsSubmitting(true);
    try {
      const { payUrl } = await submitShipping(orderId, {
        name: formData.name,
        phone: formData.phone,
        cityRef: formData.city!.Ref,
        cityName: formData.city!.Description,
        warehouseRef: formData.warehouse!.Ref,
        warehouseName: formData.warehouse!.Description,
      });
      setPayUrl(payUrl);
      setIsSuccess(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'unknown');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, orderId, validateStep, setStep]);

  // Handle popular city selection
  const handlePopularCitySelect = useCallback(async (cityName: string) => {
    setIsLoading(true);

    try {
      // Search for the city
      const foundCities = await searchCities(cityName);

      if (foundCities && foundCities.length > 0) {
        // Find exact match or first result
        const exactMatch = foundCities.find(city =>
          city.Description.toLowerCase() === cityName.toLowerCase()
        );

        const cityToSelect = exactMatch || foundCities[0];

        // Set the city
        setFormData(prev => ({ ...prev, city: cityToSelect }));

        // Clear any city error
        setErrors(prev => ({ ...prev, city: null }));

        // Proceed to next step after a short delay
        setTimeout(() => {
          setStep(2);
        }, 300);
      }
    } catch (error) {
      console.error('Error selecting popular city:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    formData,
    errors,
    cities,
    warehouses,
    isLoading,
    isSubmitting,
    step,
    isSuccess,
    payUrl,
    submitError,
    handleInputChange,
    handleCitySearch,
    handleWarehouseSearch,
    validateField,
    handleNextStep,
    handlePrevStep,
    handleSubmit,
    handlePopularCitySelect,
  };
};
